import { useEffect, useState, useCallback } from "react";
import { api } from "../api";

const ACTIVE_ENV_STORAGE_KEY = "claudeAgentActiveEnvId";

export function useEnvironments() {
  const [envs, setEnvs] = useState([]);
  const [activeEnvId, setActiveEnvIdState] = useState(() => {
    try { return localStorage.getItem(ACTIVE_ENV_STORAGE_KEY) || null; }
    catch (_) { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setActiveEnvId = useCallback((id) => {
    setActiveEnvIdState(id);
    try {
      if (id) localStorage.setItem(ACTIVE_ENV_STORAGE_KEY, id);
      else localStorage.removeItem(ACTIVE_ENV_STORAGE_KEY);
    } catch (_) {}
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listEnvironments();
      setEnvs(list);
      setActiveEnvIdState((cur) => {
        if (cur && list.some((e) => e._id === cur)) return cur;
        const def = list.find((e) => e.is_default);
        const fallback = def ? def._id : (list[0]?._id || null);
        // Persist the fallback through the same code path the setter uses
        // so we have a single source of truth for what's in localStorage.
        try {
          if (fallback) localStorage.setItem(ACTIVE_ENV_STORAGE_KEY, fallback);
          else localStorage.removeItem(ACTIVE_ENV_STORAGE_KEY);
        } catch (_) {}
        return fallback;
      });
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createEnvironment = useCallback(async (name, description) => {
    const e = await api.createEnvironment(name, description || "");
    await refresh();
    setActiveEnvId(e._id);
    return e;
  }, [refresh, setActiveEnvId]);

  const renameEnvironment = useCallback(async (id, name) => {
    await api.patchEnvironment(id, { name });
    await refresh();
  }, [refresh]);

  const deleteEnvironment = useCallback(async (id) => {
    await api.deleteEnvironment(id);
    await refresh();
  }, [refresh]);

  const shareEnvironment = useCallback(async (id, payload) => {
    const updated = await api.shareEnvironment(id, payload);
    await refresh();
    return updated;
  }, [refresh]);

  return {
    envs, activeEnvId, setActiveEnvId, loading, error,
    refresh, createEnvironment, renameEnvironment, deleteEnvironment, shareEnvironment,
  };
}
